import {
    CardHeaderDetails,
    DISCOUNT_PRICE_VARIABLE,
    FREE_TRIAL_VARIABLE,
    IChargeTier,
    IContentTier,
    IOffer,
    IOffers,
    PRICE_VARIABLE
} from '@dazn/lp-web/src/types';
import {getCountry, getCurrency} from '@dazn/lp-web/src/utils/dazn/Region';
import {getHomeCountryPrice} from '@dazn/lp-web/src/utils/getHomeCountryPrice';
import {getOfferPrice} from '@dazn/lp-web/src/utils/offer';
import {PRODUCT_GROUPS} from '@dazn/lp-web/src/constants/productGroups.constants';
import {ringPassDiscountEnabled} from '@dazn/lp-web/src/features/getRingPassDiscount';
import {SUBSCRIPTION_TYPES} from '@dazn/lp-web/src/constants/subscriptionTypes.constants';
import {isMultiPromoDiscountsEnabled} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {
    checkIsPPVDiscountOfferEnabled,
    getPPVDiscountOfferValue
} from '../Header/Utils/features';
import {parseEntitlement} from '../BannerSlider/banner-slider.utils';

const getDiscountAmount = (
    chargeTierPrice: number,
    chargeTierCurrency: string,
    discountPercentage: number,
    discountPrice?: number
): number | null => {
    if (chargeTierPrice && chargeTierCurrency) {
        const discount = chargeTierPrice * (discountPercentage / 100);
        const discountAmount = discountPrice
            ? discountPrice
            : chargeTierPrice - discount;
        return getHomeCountryPrice(discountAmount, chargeTierCurrency) ?? null;
    }
    return null;
};

export function getCardHeaderDetails(
    offerDetail: IOffer | null | undefined,
    {
        showPrice,
        price,
        yearlyprice,
        showBadge,
        badgeText,
        freeTrialText,
        monthlyprice
    }: IContentTier,
    billingPlan?: string
): CardHeaderDetails {
    const details = new CardHeaderDetails();

    if (!offerDetail && showPrice) {
        details.showPrice = showPrice;
        details.price =
            billingPlan === 'Annual'
                ? yearlyprice
                : billingPlan === 'Month'
                ? monthlyprice
                : price;
        return details;
    }

    const totalFreeDays = offerDetail?.TotalFreeDays ?? 0;
    details.hasFreeTrial = totalFreeDays > 0;

    if (details.hasFreeTrial && freeTrialText) {
        details.freeTrialText = freeTrialText.replace(
            FREE_TRIAL_VARIABLE,
            totalFreeDays.toString()
        );
    }

    const chargeTier = offerDetail?.ChargeTiers.find(
        (chargeTier: IChargeTier) => {
            return (
                chargeTier?.Currency?.toLocaleLowerCase() ===
                getCurrency()?.toLocaleLowerCase()
            );
        }
    );

    if (chargeTier?.Price && chargeTier?.Currency) {
        const discount = chargeTier?.Discount;
        const discountPercentage = discount?.Percentage ?? 0;
        const discountPrice = discount?.Price;
        if (discountPercentage && !chargeTier?.Discount?.DiscountedDays) {
            details.showDiscount = true;
            details.discountPercentage = discountPercentage;
            details.discountAmount =
                getDiscountAmount(
                    chargeTier.Price,
                    chargeTier.Currency,
                    discountPercentage,
                    discountPrice
                ) ?? 0;
        }

        if (showPrice && price) {
            if (
                price.includes(PRICE_VARIABLE) ||
                (price.includes(DISCOUNT_PRICE_VARIABLE) && discountPrice)
            ) {
                const offerPrice = getOfferPrice(
                    chargeTier.Price,
                    chargeTier.Currency,
                    billingPlan === 'Annual' &&
                        offerDetail?.BillingPeriod !== 'Week'
                        ? yearlyprice
                        : billingPlan === 'Month'
                        ? monthlyprice
                        : price
                );

                details.priceFrom = offerPrice.prefix;
                details.price = offerPrice.price;
                details.priceLabel = offerPrice.suffix;
                details.showPrice = Boolean(details.price);
            } else {
                details.priceLabel = price;
            }
        }
        if (
            checkIsPPVDiscountOfferEnabled() &&
            getPPVDiscountOfferValue()?.[getCountry()?.toLowerCase()]
        ) {
            if (offerDetail?.BillingPeriod === 'Instalments') {
                details.showDiscount = true;
                details.discountAmount =
                    getHomeCountryPrice(
                        getPPVDiscountOfferValue()?.[
                            getCountry()?.toLowerCase()
                        ],
                        chargeTier.Currency
                    ) ?? null;
            }
        }
    }

    if (showBadge && badgeText) {
        details.badgeText = badgeText.replace(
            '%{discount}',
            details.discountPercentage?.toString() || ''
        );
        details.showBadge = Boolean(details.badgeText);
    }

    const isFreeTrialEnabled = checkIfFreeTrialCanBeApplied(offerDetail);

    if (isFreeTrialEnabled && freeTrialText) {
        const chargeTier = getOfferChargeTier(offerDetail);
        const freeTrialDays = chargeTier?.Discount?.DiscountedDays ?? 0;
        details.hasFreeTrial = true;
        details.freeTrialText = freeTrialText?.replace(
            '{noOfDays}',
            freeTrialDays.toString()
        );
    }

    return details;
}

interface IGetOffer {
    offers: IOffers | null;
    promoOffers: IOffers | null;
    entitlementSetId: string;
    billingPeriod: string;
    multiplePromoOffers: IOffers[] | null;
    billingPlan?: string;
    planSelectorShown?: boolean;
}
export function filterDiscountedOffers(data: any[]) {
    if (data?.length === 1) {
        return data[0];
    } else {
        const discountedOffer = data?.find((obj) =>
            obj?.Offers?.some((offer: any) =>
                offer?.ChargeTiers?.some((tier: any) => tier?.Discount !== null)
            )
        );
        return discountedOffer ? discountedOffer : data[0];
    }
}
export function getOffer({
    offers,
    promoOffers,
    multiplePromoOffers,
    entitlementSetId,
    billingPeriod,
    billingPlan,
    planSelectorShown
}: IGetOffer): IOffer | null {
    const billingPeriodToMatch = planSelectorShown
        ? billingPeriod !== 'Week'
            ? billingPlan
            : billingPeriod
        : billingPeriod;

    const filteredData = multiplePromoOffers
        ? multiplePromoOffers
              ?.map((obj: IOffers) => {
                  const filteredOffers =
                      obj && obj?.Offers && obj?.Offers?.length > 0
                          ? obj?.Offers?.filter(
                                (offer) =>
                                    offer?.BillingPeriod ===
                                        billingPeriodToMatch &&
                                    offer?.EntitlementSetId === entitlementSetId
                            )
                          : [];

                  return {
                      ...obj,
                      Offers: filteredOffers
                  };
              })
              .filter((obj) => obj === undefined || obj?.Offers?.length > 0)
        : [];

    const finalPromoOffer =
        isMultiPromoDiscountsEnabled && filteredData
            ? filterDiscountedOffers(filteredData)
            : promoOffers;

    const finalOffers =
        (isMultiPromoDiscountsEnabled &&
            filteredData &&
            filteredData.length > 0) ||
        (promoOffers && promoOffers?.Offers?.length > 0)
            ? finalPromoOffer
            : offers;

    const hasAutoRenew = entitlementSetId?.includes('#autoRenew');
    const result = entitlementSetId && parseEntitlement(entitlementSetId);
    const entitlementId =
        typeof result === 'string' ? result : result?.entitlementSetId;
    const autoRenew = typeof result === 'string' ? null : result?.autoRenew;

    const filteredOffers = finalOffers?.Offers?.filter((offer: IOffer) => {
        return hasAutoRenew
            ? offer?.EntitlementSetId === entitlementId &&
                  offer?.BillingPeriod === billingPeriodToMatch &&
                  offer?.AutoRenew === autoRenew
            : offer?.EntitlementSetId === entitlementSetId &&
                  offer?.BillingPeriod === billingPeriodToMatch;
    });

    const offerWithDiscountedDays = filteredOffers?.find((offer: IOffer) => {
        return offer?.ChargeTiers?.some((chargeTier: IChargeTier) => {
            return chargeTier?.Discount && chargeTier?.Discount?.DiscountedDays;
        });
    });

    if (offerWithDiscountedDays) {
        return offerWithDiscountedDays;
    }

    return filteredOffers?.[0] ?? null;
}

export function getCurrentProductGroup() {
    const {pathname} = window.location;
    const pathName = pathname.split('/').filter(Boolean);
    let pageId = !!pathName.length ? pathName.pop() : 'welcome';

    let productGroup: keyof typeof PRODUCT_GROUPS;

    for (productGroup in PRODUCT_GROUPS) {
        if (PRODUCT_GROUPS[productGroup] === pageId?.toUpperCase()) {
            return PRODUCT_GROUPS[productGroup];
        }
    }

    return PRODUCT_GROUPS.DAZN;
}

export function getOfferChargeTier(offer?: IOffer | null) {
    if (!offer) return null;

    const chargeTier: IChargeTier | undefined = offer?.ChargeTiers?.find(
        ({Currency}: IChargeTier) => {
            return Currency.toUpperCase() === getCurrency()?.toUpperCase();
        }
    );

    return chargeTier;
}

export function checkIfFreeTrialCanBeApplied(offer?: IOffer | null) {
    if (!offer) return false;

    const chargeTier = getOfferChargeTier(offer);

    return (
        ringPassDiscountEnabled() &&
        chargeTier?.Discount &&
        chargeTier?.Discount?.DiscountedDays > 0 &&
        chargeTier?.Discount?.Percentage === 100 &&
        offer?.BillingPeriod === SUBSCRIPTION_TYPES.MONTHLY &&
        getCurrentProductGroup() === PRODUCT_GROUPS.DAZN
    );
}

export function getPPVContentTierTitle({
    title = '',
    offers = null,
    entitlementSetId = ''
}: {
    title?: string;
    offers?: any | null;
    entitlementSetId?: string;
}): any {
    if (title?.includes('price') && offers.Addons && entitlementSetId) {
        const price = offers.Addons?.find((addon: any) => {
            return addon.EntitlementSetId === entitlementSetId;
        });
        if (price) {
            const chargeTier = price?.ChargeTiers.find(
                (chargeTier: IChargeTier) => {
                    return (
                        chargeTier?.Currency?.toLocaleLowerCase() ===
                        getCurrency()?.toLocaleLowerCase()
                    );
                }
            );

            const offerPrice = getOfferPrice(
                chargeTier.Price,
                chargeTier.Currency,
                title
            );
            const updatedTitle = title.replace('{price}', offerPrice?.price);
            return {updatedTitle: updatedTitle, price: offerPrice?.price};
        }
    }
    return {
        updatedTitle: title,
        price: null
    };
}
